import { useState, useEffect, useRef, useCallback } from 'react';

/** Animated fallback screensaver with clock — used on native APK + web video fallback */
function ScreensaverFallback() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const timeStr = time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const dateStr = time.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#0a1628]">
      {/* Rotating gold gradient orb */}
      <div className="absolute inset-0">
        <div
          className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, #c8a960 0%, transparent 70%)',
            animation: 'pulse 4s ease-in-out infinite',
          }}
        />
        <div
          className="absolute -left-1/2 -top-1/2 h-[200%] w-[200%] opacity-10"
          style={{
            background: 'conic-gradient(from 0deg, transparent, #c8a960 8%, transparent 16%)',
            animation: 'spin 60s linear infinite',
          }}
        />
      </div>
      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-mono text-7xl font-light tabular-nums tracking-wider text-white/90">
          {timeStr}
        </div>
        <div className="mt-3 text-lg capitalize tracking-wide text-[#c8a960]/60">{dateStr}</div>
        <div className="mt-12 text-center">
          <div
            className="text-3xl font-bold tracking-wide"
            style={{
              background: 'linear-gradient(90deg, #c8a960, #e8d5a0, #c8a960)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'shimmer 3s linear infinite',
            }}
          >
            VisionPOS
          </div>
          <div className="mt-1 text-xs uppercase tracking-[0.5em] text-[#c8a960]/30">360</div>
        </div>
      </div>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>
    </div>
  );
}

interface ScreensaverProps {
  /** Idle timeout in milliseconds before screensaver activates (default: 2 minutes) */
  idleTimeout?: number;
  /** Video source URL */
  videoSrc?: string;
}

/** Detect Capacitor native environment (APK) */
const isNativeApp = () => {
  try {
    return (
      !!(window as any).Capacitor?.isNativePlatform?.() || !!(window as any).Capacitor?.isNative
    );
  } catch {
    return false;
  }
};

export default function Screensaver({
  idleTimeout = 2 * 60 * 1000,
  videoSrc = '/screensaver.mp4',
}: ScreensaverProps) {
  const [active, setActive] = useState(false);
  const [fading, setFading] = useState(false);
  // Vidéo de screensaver désactivée côté navigateur (clock fallback uniquement)
  // → supprime le /screensaver.mp4 (~24 MB) téléchargé à chaque session web.
  // La vidéo reste disponible côté natif APK si forcée via prop.
  const [videoError, setVideoError] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);

  // Use refs to avoid stale closures and dependency cycles
  const lastActivityRef = useRef(Date.now());
  const activeRef = useRef(false);
  const videoLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Track user activity via a simple timestamp (no dependency on active)
  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    // If screensaver is showing, dismiss it
    if (activeRef.current) {
      setFading(true);
      setTimeout(() => {
        activeRef.current = false;
        setActive(false);
        setFading(false);
      }, 400);
    }
  }, []);

  // Single stable effect: poll idle time every 5s
  useEffect(() => {
    const events: string[] = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));

    // Poll every 5s — avoids fragile setTimeout chains
    const poll = setInterval(() => {
      if (activeRef.current) return; // already showing
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= idleTimeout) {
        activeRef.current = true;
        setActive(true);
      }
    }, 5000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActivity));
      clearInterval(poll);
    };
  }, [idleTimeout, markActivity]);

  // Vidéo disabled globalement sur web — on garde l'effet actif pour permettre
  // réactivation future via prop (natif APK par ex) sans refactor.
  useEffect(() => {
    if (!active) return;
    setVideoError(true);
    setVideoLoading(false);
  }, [active]);

  if (!active && !fading) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] cursor-pointer"
      style={{
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.4s ease-out',
        pointerEvents: active ? 'auto' : 'none',
      }}
      onClick={markActivity}
      onTouchStart={markActivity}
    >
      {/* Clock fallback shown while video loads or if video fails */}
      {(videoError || videoLoading) && <ScreensaverFallback />}
      {/* Video layer — renders on top of clock once playing */}
      {!videoError && (
        <video
          ref={videoRef}
          src={videoSrc}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
          style={{ backgroundColor: 'transparent', opacity: videoLoading ? 0 : 1 }}
          onCanPlay={() => {
            if (videoLoadTimeoutRef.current) clearTimeout(videoLoadTimeoutRef.current);
            setVideoLoading(false);
          }}
          onError={() => {
            if (videoLoadTimeoutRef.current) clearTimeout(videoLoadTimeoutRef.current);
            setVideoError(true);
            setVideoLoading(false);
          }}
        />
      )}
      {/* Subtle touch hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-pulse select-none text-sm font-light tracking-wider text-white/40">
        Touchez pour continuer
      </div>
    </div>
  );
}
